import React, { useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface ComplexGraphData {
  labels: string[];                               
  teams?: string[];
  occurrences?: number[];
  stat_columns: string[];                   
  stat_values: Record<string, number[]>;
  axis_labels?: Record<string, string>;     
  total_records?: number;
  chartReady?: boolean;
  query_type?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const SCATTER_COLORS = [
  "#6366F1", "#EC4899", "#10B981", "#F59E0B", "#3B82F6",
  "#F97316", "#8B5CF6", "#4ADE80", "#EC4899", "#06B6D4",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtLabel = (key: string, axis_labels?: Record<string, string>) => {
  if (axis_labels?.[key]) return axis_labels[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const colorAt = (i: number) => SCATTER_COLORS[i % SCATTER_COLORS.length];

// ── Component ─────────────────────────────────────────────────────────────────
const ComplexGraph: React.FC<ComplexGraphData> = ({
  labels,
  teams,
  occurrences,
  stat_columns,
  stat_values,
  axis_labels,
  query_type,
  chartReady = true,
}) => {
  const [xKey, yKey] = stat_columns;
  const xValues = stat_values[xKey] ?? [];
  const yValues = stat_values[yKey] ?? [];
  const uniquePlayers = useMemo(() => {
    const seen = new Map<string, number>();
    const unique: string[] = [];
    labels.forEach((label, idx) => {
      if (!seen.has(label)) {
        seen.set(label, idx);
        unique.push(label);
      }
    });
    return unique;
  }, [labels]);

  // Default: all players selected
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(
    () => new Set(uniquePlayers)
  );

  const togglePlayer = (playerName: string) => {
    setSelectedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerName)) next.delete(playerName);
      else next.add(playerName);
      return next;
    });
  };

  const clearAll = () => setSelectedPlayers(new Set());

const playerColorMap = useMemo(() => {
  const map = new Map<string, string>();
  uniquePlayers.forEach((player, i) => {
    map.set(player, colorAt(i));
  });
  return map;
}, [uniquePlayers]);

  const selectAllPlayers = () => setSelectedPlayers(new Set(uniquePlayers));

  const series = useMemo(() => {
    const seriesMap = new Map<string, { name: string; data: [number, number][] }>();
    const playersToShow = selectedPlayers;

    playersToShow.forEach((playerName) => {
      seriesMap.set(playerName, { name: playerName, data: [] });
    });

    labels.forEach((playerName, idx) => {
      if (playersToShow.has(playerName)) {
        const series = seriesMap.get(playerName);
        if (series) series.data.push([xValues[idx] ?? 0, yValues[idx] ?? 0]);
      }
    });

    return Array.from(seriesMap.values());
  }, [labels, xValues, yValues, uniquePlayers, selectedPlayers]);

  const options: ApexOptions = {
    chart: {
      type: "scatter",
      toolbar: { 
        show: true,
        tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: false },
      },
      background: "transparent",
      animations: { enabled: true, speed: 600 },
      zoom: { enabled: true, type: "xy" },
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },
    colors: series.map((s) => playerColorMap.get(s.name) || "#999"),
    markers: {
      size: 8,
      strokeWidth: 2.5,
      strokeColors: "#ffffff",
      hover: { sizeOffset: 4 },
    },
    xaxis: {
      title: {
        text: fmtLabel(xKey, axis_labels),
        style: { fontSize: "12px", fontWeight: 600, color: "#374151" },
      },
      tickAmount: 6,
      labels: {
        style: { colors: "#6B7280", fontSize: "11px" },
        formatter: (val: string) => {
          const num = Number(val);
          if (!Number.isFinite(num)) return val;
          return Math.round(num).toLocaleString();
        },
      },
    },
    yaxis: {
      title: {
        text: fmtLabel(yKey, axis_labels),
        style: { fontSize: "12px", fontWeight: 600, color: "#374151" },
      },
      tickAmount: 5,
      labels: {
        style: { colors: "#6B7280", fontSize: "11px" },
        formatter: (val: number) => val.toLocaleString(),
      },
    },
    grid: {
      borderColor: "#E5E7EB",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    legend: { show: false },
    // ✅ 1ST CODE FUNCTIONALITY + 2ND CODE #d6d4d4 STYLING
    tooltip: {
  custom: ({ seriesIndex, dataPointIndex, w }: any) => {
      const name = w.globals.seriesNames[seriesIndex] as string;

      let count = -1;
      let originalIndex = -1;

      for (let i = 0; i < labels.length; i++) {
        if (labels[i] === name) {
          count++;
          if (count === dataPointIndex) {
            originalIndex = i;
            break;
          }
        }
      }

      const color = playerColorMap.get(name) || "#999";
      const team = teams?.[originalIndex] ?? "";
      const occ = occurrences?.[originalIndex];

      const statsRows = stat_columns.map((key) => {
      const value = stat_values[key]?.[originalIndex] ?? 0;

        return `
          <div style="display:flex;justify-content:space-between;">
            <span style="color:#555;font-weight:500;">
              ${fmtLabel(key, axis_labels)}:
            </span>
            <span style="font-weight:700;color:#333">
              ${Number(value).toLocaleString()}
            </span>
          </div>
        `;
      }).join("");

      return `
        <div style="
          background:#d6d4d4;
          color:#333;
          border:1px solid ${color};
          border-radius:12px;
          padding:12px 16px;
          font-family:'Inter', sans-serif;
          box-shadow:0 8px 24px rgba(0,0,0,0.15);
          min-width:200px;
        ">

          <p style="margin:0 0 8px;font-weight:700;font-size:14px">${name}</p>

          ${team ? `<p style="margin:0 0 10px;font-size:11px;color:#666">${team}</p>` : ""}

          <div style="display:flex;flex-direction:column;gap:6px;font-size:12px;">
            ${statsRows}

            ${occ !== undefined ? `
              <div style="border-top:1px solid #ccc;padding-top:8px;margin-top:4px;">
                <span style="color:#888;font-size:11px;">
                  Occurrences: <b style="color:${color}">${occ}</b>
                </span>
              </div>
            ` : ""}
          </div>

        </div>
      `;
    },
  },
  };

  if (!chartReady) return null;

  // ── Unsupported: more than 2 stat columns ──────────────────────────────────
  if (stat_columns.length > 2) {
    return (
      <div style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#FFFFFF",
        borderRadius: "16px",
        boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        padding: "40px",
        gap: "16px",
      }}>
        <div style={{ fontSize: "48px" }}>📊</div>
        <h3 style={{
          margin: 0,
          fontSize: "20px",
          fontWeight: 700,
          color: "#1E293B",
          fontFamily: "'Inter', sans-serif",
        }}>
          Graph Not Supported
        </h3>
        <p style={{
          margin: 0,
          fontSize: "14px",
          color: "#6B7280",
          textAlign: "center",
          maxWidth: "320px",
          fontFamily: "'Inter', sans-serif",
          lineHeight: 1.6,
        }}>
          This chart supports up to <strong>2 stat columns</strong>, but{" "}
          <strong>{stat_columns.length}</strong> were provided. Please reduce the
          number of stats to visualize.
        </p>
        
      </div>
    );
  }

  return (
    <div style={{ 
      width: "100%", 
      height: "100%",
      display: "flex",
      flexDirection: "column",
      background: "#FFFFFF",
      borderRadius: "16px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
      overflow: "hidden",
    }}>
      
      {/* ── Clean Header ── */}
      <div style={{
        padding: "20px 24px 16px",
        background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
        borderBottom: "1px solid #E2E8F0",
      }}>
        {/* Title & Controls */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "16px"
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
            color: "#1E293B",
            fontFamily: "'Inter', sans-serif"
          }}>
            {query_type || 'Player Performance'}
          </h3>
          <div style={{ display: "flex", gap: "12px" }}>
            <button 
              onClick={selectAllPlayers}
              style={buttonStyle("#3B82F6", "🎯")}
            >
              Select All
            </button>
            <button 
              onClick={clearAll}
              style={buttonStyle("#EF4444", "🗑️")}
            >
              Clear
            </button>
          </div>
        </div>

        {/* ── Improved Legend (2 rows max) ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: "8px",
          maxHeight: "120px",
          overflowY: "auto",
          paddingRight: "4px",
          scrollbarWidth: "thin",
        }}>
          {uniquePlayers.slice(0, 16).map((name, uniqueIdx) => {
            const firstIdx = labels.indexOf(name);
            const isActiveFilter = selectedPlayers.has(name);
            const color = colorAt(uniqueIdx);

            return (
              <div
                key={`${name}-${uniqueIdx}`}
                onClick={() => togglePlayer(name)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  background: isActiveFilter 
                    ? `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)` 
                    : "#FFFFFF",
                  border: `2px solid ${isActiveFilter ? color : "transparent"}`,
                  boxShadow: isActiveFilter 
                    ? `0 4px 12px ${color}20` 
                    : "0 1px 3px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#374151",
                }}
                title={name} 
              >
                <div style={{
                  width: 12, height: 12, 
                  borderRadius: "50%",
                  background: color,
                  boxShadow: `0 0 8px ${color}40`,
                  flexShrink: 0,
                }} />
               <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    lineHeight: "1.2",
                  }}
                >
                  <span style={{ whiteSpace: "nowrap" }}>
                    {name}
                  </span>

                  {teams?.[firstIdx] && (
                    <span
                      style={{
                        fontSize: "11px", 
                        color: "#6B7280", 
                        padding: "2px 6px",
                        background: "rgba(107,114,128,0.1)",
                        borderRadius: "6px",
                        width:"fit-content",
                        marginTop: "2px",
                      }}
                    >
                      {teams[firstIdx]}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Full Chart Area ── */}
      <div style={{
        flex: 1,
        padding: "24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 0,
      }}>
        <div style={{ width: "100%", height: "100%", minHeight: "300px" }}>
          {series.length > 0 ? (
            <ReactApexChart
              type="scatter"
              options={options}
              series={series as any}
              height="100%"
              width="100%"
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#94a3b8", fontSize: 13, fontWeight: 600, gap: 8 }}>
              <span style={{ fontSize: 28, opacity: 0.35 }}>📭</span>
              <span>No players selected</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Button Style Helper ──
const buttonStyle = (color: string, icon: string) => ({
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: 600,
  color,
  background: `${color}10`,
  border: `1px solid ${color}30`,
  borderRadius: "10px",
  cursor: "pointer",
  fontFamily: "'Inter', sans-serif",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  transition: "all 0.2s ease",
  ":hover": {
    background: `${color}20`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${color}20`,
  },
});

export default ComplexGraph;